import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Upload, MessageCircle, Zap, CheckCircle, Truck, Layers, Image as ImageIcon, FileText, ArrowRight, AlertTriangle, Palette, Droplet, Camera } from 'lucide-react';
import { Button } from '../components/ui/Button';
const faqs = [{
  question: 'What is your turnaround time for DTF transfers?',
  answer: 'We print and ship DTF transfers daily. Most orders ship within 1-2 business days after artwork approval. Rush production is available for time-sensitive projects.'
}, {
  question: 'How long does shipping take?',
  answer: 'We use UPS Ground shipping, which typically takes 3-5 business days within the continental U.S. Expedited shipping options are available at checkout for faster delivery.'
}, {
  question: 'Do you have minimum order requirements?',
  answer: 'No minimums. Order as few or as many transfers as you need. We support both small test runs and large production orders.'
}, {
  question: 'What file format do you need for artwork?',
  answer: 'PNG files with transparent backgrounds work best. Do not mirror your design—we handle that in production. We are not responsible for color issues if files are not submitted as PNG.'
}, {
  question: 'How durable are DTF transfers?',
  answer: 'DTF transfers are highly durable and designed for repeated washing. They maintain color vibrancy and flexibility through normal wear and care when applied correctly.'
}, {
  question: 'Can I upload my own gang sheet?',
  answer: 'Yes. If you have a pre-built gang sheet ready to print, you can upload it directly. We also offer a gang sheet builder if you need to arrange multiple designs.'
}];
const CTAButtons = () => <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-861">
    <a href="#builder" className="w-full sm:w-auto" data-id="element-862">
      <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-863">
        <Upload className="w-5 h-5" data-id="element-864" />
        Upload DTF Files / Order Now
      </Button>
    </a>
    <a href="sms:+15174149232" className="w-full sm:w-auto" data-id="element-865">
      <Button variant="outline" size="lg" className="w-full sm:w-auto gap-2" data-id="element-866">
        <MessageCircle className="w-5 h-5" data-id="element-867" />
        Text Us
      </Button>
    </a>
    <a href="https://m.me/atomicMI" target="_blank" rel="noopener noreferrer" title="Facebook Message" aria-label="Facebook Message" className="w-full sm:w-auto" data-id="element-868">
      <Button variant="ghost" size="lg" className="w-full sm:w-auto gap-2 bg-atomic-charcoal hover:bg-atomic-charcoal/80 border border-white/10" data-id="element-869">
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true" data-id="element-870">
          <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2V22l2.95-1.62c.84.23 1.72.36 2.65.36h.25c5.64 0 9.75-4.13 9.75-9.66C20.75 6.13 17.64 2 12 2zm1.08 13.02l-2.5-2.67-4.87 2.67 5.36-5.69 2.56 2.67 4.81-2.67-5.36 5.69z" data-id="element-871" />
        </svg>
        Facebook Message
      </Button>
    </a>
  </div>;
export function DtfPrinting() {
  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    document.title = 'DTF Transfers — Next-Day Printing & Nationwide Shipping | Print Atomic';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.setAttribute('name', 'description');
    meta.setAttribute('content', 'Order custom DTF transfers with next-day printing and nationwide shipping. No minimums, vibrant colors, and industrial durability. Based in Michigan, shipping across the U.S.');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(meta);
    }
    return () => {
      document.title = 'Print Atomic';
    };
  }, []);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    // Handle file drop logic here
  };
  return <div className="min-h-screen bg-atomic-black" data-id="element-872">
      {/* 1. Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-id="element-873">
        {/* Background image */}
        <div className="absolute inset-0 z-0" data-id="element-874">
          <img src="https://cdn.magicpatterns.com/uploads/tC99LWodMhRt2yFB3Yzi67/DTF_Transfer_Sheets_Shipped_Nationwide.jpg" alt="DTF transfer sheets laid out on production table ready for nationwide shipping" className="absolute inset-0 w-full h-full object-cover" data-id="element-875" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-876" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-877" />
        </div>
        <div className="absolute inset-0 opacity-5 z-0" style={{
        backgroundImage: 'linear-gradient(rgba(163, 230, 53, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(163, 230, 53, 0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} data-id="element-878" />
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-879">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-4xl mx-auto text-center" data-id="element-880">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" data-id="element-881">
              DTF Transfers —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-882">
                Printed & Shipped Nationwide
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-883">
              We print and ship high-quality Direct-to-Film transfers daily.
              Fast turnaround, vibrant colors, and durability you can trust.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. Gang Sheet Builder Section — moved directly below hero */}
      <section id="builder" className="py-20 bg-atomic-charcoal border-y border-white/10" data-id="element-884">
        <div className="container mx-auto px-4 md:px-6" data-id="element-885">
          <div className="max-w-2xl mx-auto" data-id="element-886">
            <div className="text-center mb-8" data-id="element-887">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-888">
                DTF Gang Sheet Builder
              </h2>
              <p className="text-lg text-gray-400" data-id="element-889">
                Upload your designs to create a custom gang sheet
              </p>
            </div>

            <div className="flex flex-col items-center gap-6" data-id="element-890">
              {/* Primary Button */}
              <Button size="lg" className="min-w-[200px]" onClick={() => {
              document.getElementById('file-input')?.click();
            }} data-id="element-891">
                <Upload className="mr-2 w-5 h-5" data-id="element-892" />
                Select Image
              </Button>
              <input id="file-input" type="file" accept="image/*" className="hidden" onChange={e => {
              // Handle file selection
            }} data-id="element-893" />

              {/* OR Divider */}
              <span className="text-gray-500 font-medium text-sm" data-id="element-894">OR</span>

              {/* Drop Zone */}
              <motion.div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} animate={{
              borderColor: isDragging ? 'rgb(163, 230, 53)' : 'rgba(255, 255, 255, 0.2)',
              backgroundColor: isDragging ? 'rgba(163, 230, 53, 0.05)' : 'transparent'
            }} className="w-full border-2 border-dashed rounded-xl p-12 md:p-16 transition-colors cursor-pointer hover:border-atomic-green/50 hover:bg-atomic-green/5" onClick={() => document.getElementById('file-input')?.click()} data-id="element-895">
                <div className="flex flex-col items-center justify-center gap-4 text-center" data-id="element-896">
                  <div className="w-16 h-16 rounded-full bg-atomic-dark flex items-center justify-center text-atomic-green border border-white/10" data-id="element-897">
                    <Upload size={32} data-id="element-898" />
                  </div>
                  <p className="text-lg font-medium text-gray-300" data-id="element-899">
                    Drop Image Here
                  </p>
                </div>
              </motion.div>

              {/* OR Divider */}
              <span className="text-gray-500 font-medium text-sm" data-id="element-900">OR</span>

              {/* Secondary Button */}
              <div className="flex flex-col items-center gap-2" data-id="element-901">
                <Button variant="outline" size="lg" className="min-w-[280px]" data-id="element-902">
                  <ImageIcon className="mr-2 w-5 h-5" data-id="element-903" />
                  Add Image from My Library
                </Button>
                <p className="text-xs text-gray-500 italic" data-id="element-904">
                  You should log in to use this feature
                </p>
              </div>
            </div>

            {/* Already Ganged CTA - GREEN */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mt-12 text-center" data-id="element-905">
              <a href="/upload-gang-sheet" data-id="element-906">
                <Button size="lg" data-id="element-907">
                  Artwork Already Ganged? CLICK HERE
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-908" />
                </Button>
              </a>
            </motion.div>

            {/* Upload Guidelines */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-atomic-black rounded-xl p-6 border border-white/10 mt-8" data-id="element-909">
              <div className="flex items-center gap-2 mb-4" data-id="element-910">
                <FileText size={20} className="text-atomic-green" data-id="element-911" />
                <h3 className="text-lg font-bold text-white" data-id="element-912">
                  Upload Guidelines
                </h3>
              </div>
              <ul className="space-y-2 text-gray-300" data-id="element-913">
                <li className="flex items-start gap-2" data-id="element-914">
                  <span className="text-atomic-green mt-1" data-id="element-915">•</span>
                  <span data-id="element-916">PNG files only (transparent background)</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-917">
                  <span className="text-atomic-green mt-1" data-id="element-918">•</span>
                  <span data-id="element-919">Do not mirror your design</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-920">
                  <span className="text-atomic-green mt-1" data-id="element-921">•</span>
                  <span data-id="element-922">
                    We are not responsible for color issues if files are not PNG
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. What DTF Is / Who It's For */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-923">
        <div className="container mx-auto px-4 md:px-6" data-id="element-924">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-925">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-926">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-927">
                What is DTF Printing?
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed" data-id="element-928">
                Direct-to-Film (DTF) is a revolutionary printing method where
                designs are printed onto a special film, coated with adhesive
                powder, and cured. You simply heat press the transfer onto your
                fabric — no weeding, no layering, and no setup screens required.
              </p>
              <ul className="space-y-3 text-gray-400" data-id="element-929">
                <li className="flex items-center gap-3" data-id="element-930">
                  <CheckCircle className="text-atomic-green w-5 h-5" data-id="element-931" />
                  <span data-id="element-932">Works on cotton, polyester, blends, and more</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-933">
                  <CheckCircle className="text-atomic-green w-5 h-5" data-id="element-934" />
                  <span data-id="element-935">Vibrant on both dark and light colored fabrics</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-936">
                  <CheckCircle className="text-atomic-green w-5 h-5" data-id="element-937" />
                  <span data-id="element-938">
                    Perfect for small businesses, print shops, and brands
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-atomic-charcoal rounded-xl p-8 border border-white/10" data-id="element-939">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-940">
                Who It's For
              </h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-941">
                <div className="bg-atomic-black p-4 rounded-lg border border-white/5 text-center" data-id="element-942">
                  <Layers className="w-8 h-8 text-atomic-green mx-auto mb-2" data-id="element-943" />
                  <span className="text-gray-300 font-medium" data-id="element-944">Print Shops</span>
                </div>
                <div className="bg-atomic-black p-4 rounded-lg border border-white/5 text-center" data-id="element-945">
                  <Zap className="w-8 h-8 text-atomic-green mx-auto mb-2" data-id="element-946" />
                  <span className="text-gray-300 font-medium" data-id="element-947">Brands</span>
                </div>
                <div className="bg-atomic-black p-4 rounded-lg border border-white/5 text-center" data-id="element-948">
                  <CheckCircle className="w-8 h-8 text-atomic-green mx-auto mb-2" data-id="element-949" />
                  <span className="text-gray-300 font-medium" data-id="element-950">Teams</span>
                </div>
                <div className="bg-atomic-black p-4 rounded-lg border border-white/5 text-center" data-id="element-951">
                  <Truck className="w-8 h-8 text-atomic-green mx-auto mb-2" data-id="element-952" />
                  <span className="text-gray-300 font-medium" data-id="element-953">Resellers</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. CTA Block #1 */}
      <section className="py-12 bg-atomic-charcoal border-y border-white/10" data-id="element-954">
        <div className="container mx-auto px-4 md:px-6" data-id="element-955">
          <CTAButtons data-id="element-956" />
        </div>
      </section>

      {/* 5. Quality Advantage */}
      <section className="py-20 bg-atomic-black" data-id="element-957">
        <div className="container mx-auto px-4 md:px-6" data-id="element-958">
          <div className="text-center mb-12" data-id="element-959">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-960">
              The Atomic Quality Advantage
            </h2>
            <p className="text-lg text-gray-400" data-id="element-961">
              Not all DTF transfers are created equal. Here's why ours stand
              out.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-962">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-atomic-charcoal p-6 rounded-xl border border-white/10" data-id="element-963">
              <div className="w-12 h-12 bg-atomic-green/10 rounded-lg flex items-center justify-center text-atomic-green mb-4" data-id="element-964">
                <Droplet className="w-6 h-6" data-id="element-965" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-966">
                Soft, Smooth Feel
              </h3>
              <p className="text-gray-400" data-id="element-967">
                Our transfers are refined to feel soft on the shirt, not thick
                or plasticky like cheaper alternatives.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-atomic-charcoal p-6 rounded-xl border border-white/10" data-id="element-968">
              <div className="w-12 h-12 bg-atomic-green/10 rounded-lg flex items-center justify-center text-atomic-green mb-4" data-id="element-969">
                <CheckCircle className="w-6 h-6" data-id="element-970" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-971">
                Long-Lasting Durability
              </h3>
              <p className="text-gray-400" data-id="element-972">
                Tested for 50+ washes. Our industrial curing process ensures
                prints won't crack, peel, or fade prematurely.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-atomic-charcoal p-6 rounded-xl border border-white/10" data-id="element-973">
              <div className="w-12 h-12 bg-atomic-green/10 rounded-lg flex items-center justify-center text-atomic-green mb-4" data-id="element-974">
                <Palette className="w-6 h-6" data-id="element-975" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-976">
                Vibrant Full Color
              </h3>
              <p className="text-gray-400" data-id="element-977">
                We use premium inks and industrial equipment to produce bright,
                accurate colors and crisp details.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5.5 DTF Examples */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-978">
        <div className="container mx-auto px-4 md:px-6" data-id="element-979">
          <div className="text-center mb-12" data-id="element-980">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-981">
              DTF Examples
            </h2>
            <p className="text-lg text-gray-400" data-id="element-982">
              See the quality difference for yourself
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-983">
            {[{
            src: 'https://cdn.magicpatterns.com/uploads/cd1K1C28ZrMoL6wiNaekTq/DTF_Transfer_Sheets_Shipped_Nationwide.jpg',
            alt: 'DTF transfer sheets ready for nationwide shipping',
            label: 'DTF Transfer Sheets'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/jh9bqjmaiqt338Dvo2EecH/Pressed_on_Fabric_-_Custom_DTF_Transfers.jpg',
            alt: 'Custom DTF transfer pressed onto fabric hoodie',
            label: 'Pressed on Fabric'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/2A2QVyyoEex7NAcsZzdNo6/Shipping_Prep_-_DTF_Transfers.jpg',
            alt: 'DTF transfers packed and ready for shipping',
            label: 'Shipping Prep'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/rNQLzhb12fokvntpWwFcdn/Close-up_Detail_of_our_DTF_Transfers_pressed_onto_apparel.jpg',
            alt: 'Close-up detail of DTF transfer on apparel showing vibrant colors',
            label: 'Close-up Detail'
          }].map((image, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group rounded-xl overflow-hidden border border-white/10 hover:border-atomic-green/30 transition-colors" data-id="element-984">
                <div className="aspect-square overflow-hidden" data-id="element-985">
                  <img src={image.src} alt={image.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-986" />
                </div>
                <div className="bg-atomic-black px-4 py-3" data-id="element-987">
                  <span className="text-sm text-gray-300 font-medium" data-id="element-988">
                    {image.label}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. Next-Day Shipping & No Minimums */}
      <section className="py-20 bg-atomic-charcoal border-y border-white/10" data-id="element-989">
        <div className="container mx-auto px-4 md:px-6" data-id="element-990">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-991">
            {/* Next Day Shipping Card */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-atomic-green/20 to-atomic-black p-8 rounded-2xl border border-atomic-green/30 relative overflow-hidden group" data-id="element-992">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-993">
                <Truck className="w-32 h-32 text-atomic-green" data-id="element-994" />
              </div>
              <div className="relative z-10" data-id="element-995">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-atomic-green text-black text-xs font-bold mb-4" data-id="element-996">
                  FAST TURNAROUND
                </div>
                <h3 className="text-3xl font-bold text-white mb-4" data-id="element-997">
                  Next-Day Shipping
                </h3>
                <ul className="space-y-3 text-gray-300" data-id="element-998">
                  <li className="flex items-center gap-2" data-id="element-999">
                    <CheckCircle className="w-5 h-5 text-atomic-green" data-id="element-1000" />
                    <span data-id="element-1001">Most orders ship within 24 hours</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1002">
                    <CheckCircle className="w-5 h-5 text-atomic-green" data-id="element-1003" />
                    <span data-id="element-1004">Free UPS Ground on orders over $200</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1005">
                    <CheckCircle className="w-5 h-5 text-atomic-green" data-id="element-1006" />
                    <span data-id="element-1007">Local pickup available in Morrice, MI</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* No Minimums Card */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-atomic-black p-8 rounded-2xl border border-white/20 relative overflow-hidden group hover:border-atomic-green/30 transition-colors" data-id="element-1008">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-1009">
                <Layers className="w-32 h-32 text-white" data-id="element-1010" />
              </div>
              <div className="relative z-10" data-id="element-1011">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-bold mb-4 border border-white/10" data-id="element-1012">
                  FLEXIBLE ORDERING
                </div>
                <h3 className="text-3xl font-bold text-white mb-4" data-id="element-1013">
                  No Minimums
                </h3>
                <ul className="space-y-3 text-gray-300" data-id="element-1014">
                  <li className="flex items-center gap-2" data-id="element-1015">
                    <CheckCircle className="w-5 h-5 text-atomic-green" data-id="element-1016" />
                    <span data-id="element-1017">Order 1 sheet or 1,000</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1018">
                    <CheckCircle className="w-5 h-5 text-atomic-green" data-id="element-1019" />
                    <span data-id="element-1020">Same industrial quality for every order</span>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-1021">
                    <CheckCircle className="w-5 h-5 text-atomic-green" data-id="element-1022" />
                    <span data-id="element-1023">Perfect for prototypes and test runs</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. Upload/Order System Explanation */}
      <section className="py-20 bg-atomic-black" data-id="element-1024">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1025">
          <div className="max-w-4xl mx-auto" data-id="element-1026">
            <div className="text-center mb-12" data-id="element-1027">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1028">
                Perfect Prints, Every Time
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1029">
                We review every file before printing to ensure the best results
                — reducing your risk and saving you time.
              </p>
            </div>

            <div className="bg-atomic-charcoal rounded-xl p-8 border border-white/10" data-id="element-1030">
              {/* How Our System Works */}
              <div className="mb-8 pb-8 border-b border-white/10" data-id="element-1031">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-1032">
                  <Zap className="text-atomic-green w-5 h-5" data-id="element-1033" />
                  How Our Upload System Works
                </h3>
                <ul className="space-y-3 text-gray-400" data-id="element-1034">
                  <li className="flex items-start gap-2" data-id="element-1035">
                    <span className="text-atomic-green mt-1" data-id="element-1036">•</span>
                    <span data-id="element-1037">
                      <strong className="text-gray-200" data-id="element-1038">
                        Preview before you pay
                      </strong>{' '}
                      — see exactly how your design will look on the gang sheet
                      before we print
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1039">
                    <span className="text-atomic-green mt-1" data-id="element-1040">•</span>
                    <span data-id="element-1041">
                      <strong className="text-gray-200" data-id="element-1042">
                        Automatic issue detection
                      </strong>{' '}
                      — our system flags transparency problems, white background
                      boxes, and low-resolution files before printing
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1043">
                    <span className="text-atomic-green mt-1" data-id="element-1044">•</span>
                    <span data-id="element-1045">
                      <strong className="text-gray-200" data-id="element-1046">Reduced risk</strong> —
                      catch mistakes before they cost you money. We review every
                      order to make sure it prints right the first time
                    </span>
                  </li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-8" data-id="element-1047">
                <div data-id="element-1048">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-1049">
                    <AlertTriangle className="text-amber-500 w-5 h-5" data-id="element-1050" />
                    Common Issues We Flag
                  </h3>
                  <ul className="space-y-3 text-gray-400" data-id="element-1051">
                    <li className="flex items-start gap-2" data-id="element-1052">
                      <span className="text-amber-500 mt-1" data-id="element-1053">•</span>
                      <span data-id="element-1054">Transparency issues / semi-transparent pixels</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1055">
                      <span className="text-amber-500 mt-1" data-id="element-1056">•</span>
                      <span data-id="element-1057">White background boxes around designs</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1058">
                      <span className="text-amber-500 mt-1" data-id="element-1059">•</span>
                      <span data-id="element-1060">Low resolution / blurry artwork</span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-1061">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2" data-id="element-1062">
                    <CheckCircle className="text-atomic-green w-5 h-5" data-id="element-1063" />
                    How to Get Best Results
                  </h3>
                  <ul className="space-y-3 text-gray-400" data-id="element-1064">
                    <li className="flex items-start gap-2" data-id="element-1065">
                      <span className="text-atomic-green mt-1" data-id="element-1066">•</span>
                      <span data-id="element-1067">Submit PNGs with transparent backgrounds</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1068">
                      <span className="text-atomic-green mt-1" data-id="element-1069">•</span>
                      <span data-id="element-1070">300 DPI resolution recommended</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-1071">
                      <span className="text-atomic-green mt-1" data-id="element-1072">•</span>
                      <span data-id="element-1073">Do NOT mirror your artwork (we do it)</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-white/10 text-center" data-id="element-1074">
                <p className="text-gray-400 text-sm mb-4" data-id="element-1075">
                  Note: We are not responsible for color issues if files are not
                  submitted as PNG. Need help? We offer free design assistance
                  for simple fixes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. CTA Block #2 */}
      <section className="py-12 bg-atomic-black border-t border-white/10" data-id="element-1076">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1077">
          <div className="text-center mb-8" data-id="element-1078">
            <h2 className="text-2xl font-bold text-white mb-2" data-id="element-1079">
              Ready to Print?
            </h2>
            <p className="text-gray-400" data-id="element-1080">
              Start your order or contact us for help.
            </p>
          </div>
          <CTAButtons data-id="element-1081" />
        </div>
      </section>

      {/* 9. FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-1082">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1083">
          <div className="max-w-3xl mx-auto" data-id="element-1084">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-1085">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1086">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-1087">
                Common questions about DTF printing and shipping
              </p>
            </motion.div>

            <div className="space-y-6" data-id="element-1088">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-1089">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-1090">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1091">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}